import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LupaDeImagen } from '../../../components/LupaDeImagen';
import { urlPorVencer } from './firmas';

type Estado = 'cargando' | 'ok' | 'error';

/**
 * Lightbox mínimo: una imagen, click afuera o Esc cierra, click en la imagen abre el
 * original en otra pestaña. Va por portal a document.body: el panel de contenido de
 * desktop es position:fixed y un overlay adentro queda DEBAJO de la sidebar.
 *
 * `<img>` directo, no CachedImg: es una imagen grande que se ve una vez y el caché no
 * aporta; y cuando la URL firmada (1 h) venció, CachedImg caía al src crudo, el `<img>`
 * fallaba y con `alt=""` quedaba el fondo negro y nada. Acá cada estado se VE: cargando,
 * error con la causa y salidas (abrir en pestaña nueva, reintentar), y URL ya vencida
 * sin siquiera intentar (useRefrescoFirmas la está renovando en la vista de atrás).
 *
 * Con la imagen cargada, la lupa del historial amplía donde está el cursor
 * (ADR-2026-10-13-lupa-y-alta-de-producto-en-el-test-masivo).
 */
const Lightbox: React.FC<{ url: string; onClose: () => void }> = ({ url, onClose }) => {
  const [estado, setEstado] = useState<Estado>('cargando');
  const [intento, setIntento] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  // Vencida de verdad (margen 0): con 3 min de vida todavía carga bien.
  const vencida = urlPorVencer(url, Date.now(), 0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setEstado('cargando');
  }, [url]);

  const reintentar = () => {
    setEstado('cargando');
    setIntento((i) => i + 1);
  };
  const frenar = (e: React.MouseEvent) => e.stopPropagation();
  const causa = urlPorVencer(url)
    ? 'La URL firmada venció o está por vencer (dura 1 h). Cerrá y volvé a abrir la imagen: se renueva sola.'
    : 'Puede ser la red o que el archivo ya no exista. Probá abrirla en una pestaña nueva.';

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-6" onClick={onClose}>
      {vencida ? (
        <div className="max-w-md rounded-2xl bg-white/10 p-4 text-center text-sm text-white" onClick={frenar}>
          <p className="font-medium">La URL firmada venció (dura 1 h): se está renovando…</p>
          <p className="mt-1 text-xs text-white/70">Cerrá y volvé a abrir la imagen en unos segundos.</p>
        </div>
      ) : (
        <>
          {estado === 'cargando' && (
            <div className="flex items-center gap-2 text-sm text-white" onClick={frenar}>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Cargando imagen…
            </div>
          )}
          {estado === 'error' && (
            <div className="max-w-md space-y-3 rounded-2xl bg-white/10 p-4 text-center text-sm text-white" onClick={frenar}>
              <p className="font-medium">No se pudo cargar la imagen</p>
              <p className="text-xs text-white/70">{causa}</p>
              <div className="flex flex-wrap justify-center gap-2">
                <a href={url} target="_blank" rel="noreferrer" className="rounded-full bg-white/15 py-2 px-4 text-sm text-white hover:bg-white/25">
                  Abrir en pestaña nueva
                </a>
                <button type="button" onClick={reintentar} className="rounded-full bg-white py-2 px-4 text-sm font-medium text-brand-black">
                  Reintentar
                </button>
              </div>
            </div>
          )}
          {/* Mientras carga queda fuera del flujo e invisible (sigue bajando); con error se saca para no ver el ícono roto. */}
          <a href={url} target="_blank" rel="noreferrer" title="Abrir original" onClick={frenar} className={estado === 'ok' ? '' : 'invisible absolute'}>
            <img
              ref={imgRef}
              key={`${url}#${String(intento)}`}
              src={url}
              alt="Imagen ampliada"
              decoding="async"
              onLoad={() => setEstado('ok')}
              onError={() => setEstado('error')}
              className="max-h-[90vh] max-w-full cursor-zoom-in rounded-lg object-contain"
            />
          </a>
          {/* z 61: por encima de este overlay (z-[60]). */}
          <LupaDeImagen imgRef={imgRef} src={url} habilitada={estado === 'ok'} zoom={2.5} tamano={220} zIndex={61} />
        </>
      )}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/15 py-2 px-4 text-sm text-white hover:bg-white/25"
      >
        Cerrar (Esc)
      </button>
    </div>,
    document.body,
  );
};

export default Lightbox;
